/**
 * RTK Query API slice for Admin Dashboard
 * Provides queries for admin-specific functionality including student search
 */

import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

/**
 * Mock student database for development
 * This simulates the backend student database
 */
const mockStudentDatabase = [
  {
    id: 'student001',
    name: 'Alice Johnson',
    email: 'johnson.123@osu.edu',
    programs: ['honors'],
    lastActivity: new Date('2024-01-15'),
    status: 'active',
    year: 'sophomore',
    major: 'Computer Science'
  },
  {
    id: 'student002', 
    name: 'Bob Smith',
    email: 'smith.456@osu.edu',
    programs: ['scholars'],
    lastActivity: new Date('2024-01-14'),
    status: 'active',
    year: 'freshman',
    major: 'Engineering'
  },
  {
    id: 'student003',
    name: 'Carol Davis',
    email: 'davis.789@osu.edu', 
    programs: ['honors', 'scholars'],
    lastActivity: new Date('2024-01-13'),
    status: 'active',
    year: 'junior',
    major: 'Biology'
  },
  {
    id: 'student004',
    name: 'David Wilson',
    email: 'wilson.012@osu.edu',
    programs: ['honors'],
    lastActivity: new Date('2024-01-12'),
    status: 'active',
    year: 'senior',
    major: 'Mathematics'
  },
  {
    id: 'student005',
    name: 'Eva Brown',
    email: 'brown.345@osu.edu',
    programs: ['scholars'],
    lastActivity: new Date('2024-01-11'),
    status: 'active',
    year: 'sophomore',
    major: 'Chemistry'
  },
  {
    id: 'student006',
    name: 'Frank Miller',
    email: 'miller.678@osu.edu',
    programs: ['honors'],
    lastActivity: new Date('2024-01-10'),
    status: 'active',
    year: 'freshman',
    major: 'Physics'
  },
  {
    id: 'student007',
    name: 'Grace Lee',
    email: 'lee.901@osu.edu',
    programs: ['scholars'],
    lastActivity: new Date('2024-01-09'),
    status: 'active',
    year: 'junior',
    major: 'Business'
  },
  {
    id: 'student008',
    name: 'Henry Taylor',
    email: 'taylor.234@osu.edu',
    programs: ['honors', 'scholars'],
    lastActivity: new Date('2024-01-08'),
    status: 'active',
    year: 'senior',
    major: 'Liberal Arts'
  },
  {
    id: 'student009',
    name: 'Ivy Chen',
    email: 'chen.567@osu.edu',
    programs: ['honors'],
    lastActivity: new Date('2024-01-07'),
    status: 'active',
    year: 'sophomore',
    major: 'Psychology'
  },
  {
    id: 'student010',
    name: 'Jack Anderson',
    email: 'anderson.890@osu.edu',
    programs: ['scholars'],
    lastActivity: new Date('2024-01-06'),
    status: 'active',
    year: 'freshman',
    major: 'History'
  }
];

/**
 * Mock API delay simulation
 */
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Search students function
 * @param {string} query - Search query (name, email, or ID)
 * @param {Object} options - Search options
 * @returns {Array} Filtered student results
 */
const searchStudents = (query, options = {}) => {
  if (!query || query.trim() === '') {
    return [];
  }

  const searchTerm = query.toLowerCase().trim();
  const { limit = 20, programs = [] } = options;

  let results = mockStudentDatabase.filter(student => {
    // Search by name, email, or ID
    const matchesQuery = 
      student.name.toLowerCase().includes(searchTerm) ||
      student.email.toLowerCase().includes(searchTerm) ||
      student.id.toLowerCase().includes(searchTerm) ||
      student.major.toLowerCase().includes(searchTerm);

    // Filter by programs if specified
    const matchesPrograms = programs.length === 0 || 
      programs.some(program => student.programs.includes(program));

    return matchesQuery && matchesPrograms;
  });

  // Sort by relevance (exact matches first, then partial matches)
  results.sort((a, b) => {
    const aExactMatch = 
      a.name.toLowerCase() === searchTerm ||
      a.email.toLowerCase() === searchTerm ||
      a.id.toLowerCase() === searchTerm;
    
    const bExactMatch = 
      b.name.toLowerCase() === searchTerm ||
      b.email.toLowerCase() === searchTerm ||
      b.id.toLowerCase() === searchTerm;

    if (aExactMatch && !bExactMatch) return -1;
    if (!aExactMatch && bExactMatch) return 1;

    // Secondary sort by name
    return a.name.localeCompare(b.name);
  });

  // Apply limit
  return results.slice(0, limit);
};

/**
 * Admin Dashboard API slice
 */
export const adminApi = createApi({
  reducerPath: 'adminApi',
  baseQuery: fetchBaseQuery({
    baseUrl: '/api/admin/',
    prepareHeaders: (headers, { getState }) => {
      // Add authentication headers here if needed
      // const token = getState().auth.token;
      // if (token) {
      //   headers.set('authorization', `Bearer ${token}`);
      // }
      return headers;
    }
  }),
  tagTypes: ['StudentSearch', 'StudentData', 'AdminPermissions'],
  endpoints: (builder) => ({
    searchStudents: builder.query({
      queryFn: async ({ query, limit = 20, programs = [] }) => {
        try {
          // Simulate API delay
          await delay(300);

          if (!query || query.trim() === '') {
            return { 
              data: {
                students: [],
                total: 0,
                hasMore: false,
                query: query || ''
              }
            };
          }

          const results = searchStudents(query, { limit, programs });
          const hasMore = results.length === limit; // Simple pagination check

          return {
            data: {
              students: results,
              total: results.length,
              hasMore,
              query
            }
          };
        } catch (error) {
          console.error('Student search error:', error);
          return { 
            error: { 
              status: 500, 
              data: { message: 'Failed to search students. Please try again.' } 
            } 
          };
        }
      },
      providesTags: (result, error, { query }) => [
        { type: 'StudentSearch', id: query }
      ]
    }),

    getStudentById: builder.query({
      queryFn: async ({ studentId }) => {
        try {
          // Simulate API delay
          await delay(200);

          const student = mockStudentDatabase.find(s => s.id === studentId);
          
          if (!student) {
            return { 
              error: { 
                status: 404, 
                data: { message: 'Student not found' } 
              } 
            };
          }

          return { data: student };
        } catch (error) {
          console.error('Get student error:', error);
          return { 
            error: { 
              status: 500, 
              data: { message: 'Failed to fetch student data. Please try again.' } 
            } 
          };
        }
      },
      providesTags: (result, error, { studentId }) => [
        { type: 'StudentData', id: studentId }
      ]
    }),

    getAdminPermissions: builder.query({
      queryFn: async () => {
        try {
          // Simulate API delay
          await delay(100);

          // Mock admin permissions - in real implementation this would come from JWT/auth
          return {
            data: {
              canSearchStudents: true,
              canViewHonorsStudents: true,
              canViewScholarsStudents: true,
              canPerformAdminActions: false, // Future extensibility
              canViewAuditLogs: true,
              allowedPrograms: ['honors', 'scholars']
            }
          };
        } catch (error) {
          console.error('Get admin permissions error:', error);
          return { 
            error: { 
              status: 500, 
              data: { message: 'Failed to fetch admin permissions. Please try again.' } 
            } 
          };
        }
      },
      providesTags: ['AdminPermissions']
    }),

    logAdminAccess: builder.mutation({
      queryFn: async ({ action, studentId, details = {} }) => {
        try {
          // Simulate API delay
          await delay(100);

          // Mock audit logging - in real implementation this would log to backend
          const logEntry = {
            timestamp: new Date().toISOString(),
            action,
            studentId,
            details,
            adminUser: 'current-admin-user' // Would come from auth context
          };

          return { data: { success: true, logEntry } };
        } catch (error) {
          console.error('Admin access logging error:', error);
          return { 
            error: { 
              status: 500, 
              data: { message: 'Failed to log admin access.' } 
            } 
          };
        }
      }
    })
  })
});

// Export hooks for use in components
export const {
  useSearchStudentsQuery,
  useLazySearchStudentsQuery,
  useGetStudentByIdQuery,
  useLazyGetStudentByIdQuery,
  useGetAdminPermissionsQuery,
  useLogAdminAccessMutation
} = adminApi;

// Export utility functions for testing and development
export const getMockStudentDatabase = () => [...mockStudentDatabase];

export const addMockStudent = (student) => {
  mockStudentDatabase.push({
    id: student.id || `student${Date.now()}`,
    name: student.name || 'Unknown Student',
    email: student.email || 'unknown@osu.edu',
    programs: student.programs || [],
    lastActivity: student.lastActivity || new Date(),
    status: student.status || 'active',
    year: student.year || 'unknown',
    major: student.major || 'Undeclared',
    ...student
  });
};

export const clearMockStudentDatabase = () => {
  mockStudentDatabase.length = 0;
};

export const resetMockStudentDatabase = () => {
  clearMockStudentDatabase();
  // Re-add default students
  const defaultStudents = [
    {
      id: 'student001',
      name: 'Alice Johnson',
      email: 'johnson.123@osu.edu',
      programs: ['honors'],
      lastActivity: new Date('2024-01-15'),
      status: 'active',
      year: 'sophomore',
      major: 'Computer Science'
    },
    {
      id: 'student002', 
      name: 'Bob Smith',
      email: 'smith.456@osu.edu',
      programs: ['scholars'],
      lastActivity: new Date('2024-01-14'),
      status: 'active',
      year: 'freshman',
      major: 'Engineering'
    }
    // Add more default students as needed
  ];
  
  defaultStudents.forEach(student => mockStudentDatabase.push(student));
};

export default adminApi;